import { useCallback, useRef, useState } from 'react';
import BatchService from '../../../shared/middleware/services/BatchService';
import S3JsonService from '../../../shared/middleware/services/S3JsonService';
import WorkerService from '../../../shared/workers/WorkerService';
import { createDownloadWorker } from '../../../shared/workers/downloadWorkerFactory';
import logger from '../../../shared/services/logging/logger';
import { findReportFiles, fetchReportFile, saveReport, REPORT_FORMATS } from '../../../shared/middleware/services/ReportFileService';

/**
 * Past batch runs for one grouping — the batch half of the run-history sidebar
 * in Automation Scripts.
 *
 *   list     GET /projects/{projectId}/groupings/{groupingId}/batches
 *   summary  GET …/folders/file/download?s3FolderPath=…  → presigned URL → FETCH_JSON
 *   report   GET …/folders/file/download?s3FolderPath=…  → presigned URL → DOWNLOAD_FILE
 *
 * The listing answers the whole sidebar in one call: status, pass rate, counts,
 * timings and each batch's `reportFolder`. Nothing is read from S3 to draw the
 * list.
 *
 * The summary and report reads are the same presigned-URL sequence the
 * single-run history uses, so there is one way to read a file, not two — and
 * that means handing the download endpoint the **folder**, exactly as the
 * single-run history does. The endpoint resolves the file inside it; naming
 * the file explicitly is a 404.
 *
 * @module features/executionStudio/hooks/useBatchRunHistory
 */

const IS_DEV = process.env.NODE_ENV === 'development';

/** Off-thread fetch for the report HTML; auto-terminates after 60s idle. */
const downloadWorker = new WorkerService(createDownloadWorker);

/** Server default is 20, cap is 50. */
const PAGE_SIZE = 20;

/** The summary can land a moment after the batch ends — same as single runs. */
const SUMMARY_FETCH_ATTEMPTS = 4;
const SUMMARY_RETRY_MS = 1500;

/** Batch outcome → the vocabulary the shared run-history list renders. */
const RESULT_FOR_STATUS = {
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  PARTIALLY_PASSED: 'PARTIALLY_PASSED',
  CANCELLED: 'CANCELLED',
};

/** `reportFolder` carries a trailing slash; joining must not double it. */
const join = (folder, ...parts) => [folder.replace(/\/+$/, ''), ...parts].join('/');

const durationBetween = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * One listing item to one sidebar row.
 *
 * Figures are passed through untouched. A running batch reports nulls for
 * `passRate`, `endedAt` and the counts; null means "not known yet" and must not
 * become 0, which would read as a real result.
 */
/**
 * "30/35 perms passed (86%) • 2 scripts" — the run card's result line. Null
 * while the batch is still running: its counts are not known yet, and a
 * half-written line would read as a result.
 */
const sublineFor = (batch) => {
  const { passedPermutations: passed, totalPermutations: total, passRate } = batch;
  if (passed == null || total == null) return null;
  const scripts = Array.isArray(batch.members) ? batch.members.length : null;
  const rate = typeof passRate === 'number' ? ` (${Math.round(passRate)}%)` : '';
  return `${passed}/${total} perms passed${rate}`
    + (scripts != null ? ` • ${scripts} script${scripts === 1 ? '' : 's'}` : '');
};

const toRow = (batch) => ({
  id: batch.batchId,
  batchId: batch.batchId,
  runNumber: batch.batchRunNumber,
  label: `Run ${batch.batchRunNumber}`,
  result: RESULT_FOR_STATUS[batch.status] ?? batch.status ?? null,
  overallStatus: batch.status ?? null,
  passRate: batch.passRate ?? null,
  startDateTime: batch.startedAt ?? null,
  endDateTime: batch.endedAt ?? null,
  createdAt: batch.startedAt ?? null,
  durationMs: durationBetween(batch.startedAt, batch.endedAt),
  timeZone: batch.timeZone ?? null,
  scriptsPassed: batch.scriptsPassed ?? null,
  scriptsPartial: batch.scriptsPartial ?? null,
  scriptsFailed: batch.scriptsFailed ?? null,
  totalOutOfScope: batch.totalOutOfScope ?? null,
  totalPermutations: batch.totalPermutations ?? null,
  executedPermutations: batch.executedPermutations ?? null,
  passedPermutations: batch.passedPermutations ?? null,
  failedPermutations: batch.failedPermutations ?? null,
  scriptCount: Array.isArray(batch.members) ? batch.members.length : null,
  members: batch.members ?? [],
  // Locates both files. Absent on a batch that never produced output.
  reportFolder: batch.reportFolder ?? null,
  // A batch still running has not written its summary yet
  isRunning: !batch.endedAt,
  subline: batch.endedAt ? sublineFor(batch) : null,
});

/**
 * The download endpoint takes a folder and resolves the file inside it — the
 * single-run history passes `…/runs/run28/summary` and gets its summary back.
 * Naming the file (`…/summary/consolidated-summary.json`) returns a 404.
 */
const summaryKeyFor = (run) => (run?.reportFolder ? join(run.reportFolder, 'summary') : null);

const reportKeyFor = (run) => (run?.reportFolder ? join(run.reportFolder, 'reports') : null);

export const useBatchRunHistory = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportFile, setReportFile] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Only the newest response may write — an older page must not overwrite it
  const latestRequestRef = useRef(0);
  const summaryCacheRef = useRef({});
  const pageArgsRef = useRef(null);

  /** First page of the grouping's batches, newest first. */
  const fetchRuns = useCallback(async ({ projectId, groupingId, userData }) => {
    if (!projectId || !groupingId || !userData) return [];

    const requestId = ++latestRequestRef.current;
    pageArgsRef.current = { projectId, groupingId, userData };
    setLoading(true);
    setError('');

    try {
      const { items, nextCursor: cursor } = await BatchService.listBatches(
        projectId, groupingId, { limit: PAGE_SIZE }, userData
      );
      if (requestId !== latestRequestRef.current) return [];

      const rows = (items || []).map(toRow);
      setRuns(rows);
      setNextCursor(cursor);
      setLoading(false);
      logger.info('Batch history loaded', { groupingId, count: rows.length, more: !!cursor });
      return rows;
    } catch (err) {
      if (requestId !== latestRequestRef.current) return [];
      logger.debug('Batch history unavailable', { error: err.message, groupingId });
      setRuns([]);
      setNextCursor(null);
      setError('Could not load the run history for this grouping.');
      setLoading(false);
      return [];
    }
  }, []);

  /** The next page, appended. No-op once `nextCursor` is null. */
  const loadMore = useCallback(async () => {
    const args = pageArgsRef.current;
    if (!nextCursor || !args || loadingMore) return;

    const requestId = latestRequestRef.current;
    setLoadingMore(true);
    try {
      const { items, nextCursor: cursor } = await BatchService.listBatches(
        args.projectId, args.groupingId, { limit: PAGE_SIZE, cursor: nextCursor }, args.userData
      );
      // A fresh fetchRuns while this was in flight wins
      if (requestId !== latestRequestRef.current) return;

      setRuns((prev) => [...prev, ...(items || []).map(toRow)]);
      setNextCursor(cursor);
    } catch (err) {
      logger.debug('Could not load more batches', { error: err.message });
      setError('Could not load more runs.');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore]);

  /**
   * Load one batch's consolidated summary, retried a few times to ride out S3
   * write-lag exactly as the single-run summary read does.
   */
  const selectRun = useCallback(async ({ projectId, run, userData }) => {
    if (!run) return;
    setSelectedRunId(run.id);
    setReportError('');
    // A running batch has a report folder but no report in it yet — the Core
    // writes it on completion — so no download is offered until it finishes.
    const reportFolder = run.isRunning ? null : reportKeyFor(run);
    setReportFile(reportFolder ? { s3FolderPath: reportFolder, pdfName: null } : null);
    // Whether a PDF sits beside the HTML — offered in the download menu if so.
    // Checked in the background; the HTML download never waits on it.
    if (reportFolder) {
      findReportFiles(projectId, reportFolder, userData).then(({ html, pdf }) => {
        setReportFile((current) => (current?.s3FolderPath === reportFolder
          ? { ...current, htmlName: html, pdfName: pdf }
          : current));
      });
    }

    const cached = summaryCacheRef.current[run.id];
    if (cached) {
      setReport(cached);
      return;
    }

    setReport(null);

    // Nothing to read yet — say why rather than retrying against a 404
    if (run.isRunning) {
      setReportError('This run is still going — its report is written when it finishes.');
      return;
    }

    const summaryKey = summaryKeyFor(run);
    if (!summaryKey) {
      setReportError('This run produced no report.');
      return;
    }

    setReportLoading(true);
    try {
      for (let attempt = 0; attempt < SUMMARY_FETCH_ATTEMPTS; attempt += 1) {
        if (attempt > 0) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, SUMMARY_RETRY_MS));
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          const summary = await S3JsonService.fetchJson(projectId, summaryKey, userData);
          summaryCacheRef.current[run.id] = summary;
          setReport(summary);
          return;
        } catch (err) {
          logger.debug('Summary fetch attempt failed', { run: run.id, attempt, error: err.message });
        }
      }
      setReportError('No consolidated report for this run yet.');
    } finally {
      setReportLoading(false);
    }
  }, []);

  /**
   * Download a batch's HTML report — the same presigned-URL download the
   * single-run history uses.
   *
   * Deliberately plain: the single-run path rewrites the report's date helpers
   * to local time, but that rewrite is written against the single-run report
   * generator's exact source. Applying it to a batch report sight-unseen would
   * be guesswork, so the file is saved as the Core wrote it.
   */
  const downloadReport = useCallback(async ({ projectId, groupingName, run, userData, format = 'html' }) => {
    const s3FolderPath = reportKeyFor(run);
    if (!s3FolderPath || downloading) return;

    setDownloading(true);
    try {
      const sanitize = (part) => String(part).replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
      const baseName = [groupingName || 'batch', `run${run.runNumber}`, 'consolidated-report']
        .filter(Boolean).map(sanitize).join('_');
      const { extension, mime } = REPORT_FORMATS[format] || REPORT_FORMATS.html;
      const fileName = `${baseName}.${extension}`;

      // Read fresh at download time: the folder may have gained a PDF since
      const files = await findReportFiles(projectId, s3FolderPath, userData);

      let data;
      if (format === 'pdf') {
        if (!files.pdf) throw new Error('This run has no PDF report');
        data = await fetchReportFile(projectId, s3FolderPath, files.pdf, userData);
      } else if (files.pdf && files.html) {
        // Both formats share the folder — the folder download cannot tell them
        // apart, so the HTML is asked for by name
        data = await fetchReportFile(projectId, s3FolderPath, files.html, userData);
      } else {
        const url = await S3JsonService.getPresignedUrl(projectId, s3FolderPath, userData);
        if (!url) throw new Error('No presigned URL for the report');
        data = await downloadWorker.run(
          'DOWNLOAD_FILE', IS_DEV ? { url, proxyUrl: '/s3-proxy' } : { url }
        );
      }

      saveReport(data, fileName, mime);
      logger.info('Batch report downloaded', { fileName, format });
    } catch (err) {
      logger.error('Batch report download failed', { error: err.message });
      setReportError('Could not download the report for this run.');
    } finally {
      setDownloading(false);
    }
  }, [downloading]);

  /** Forget the cached summaries so a re-run is not served the previous one. */
  const invalidate = useCallback(() => {
    summaryCacheRef.current = {};
  }, []);

  return {
    runs,
    loading,
    loadingMore,
    hasMore: !!nextCursor,
    error,
    selectedRunId,
    report,
    reportLoading,
    reportError,
    reportFile,
    downloading,
    fetchRuns,
    loadMore,
    selectRun,
    downloadReport,
    invalidate,
  };
};

export default useBatchRunHistory;
